import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import ExportToolbar from "@/components/ExportToolbar";
import DateRangePicker from "@/components/DateRangePicker";
import { useReportRange, formatRangeLabel, formatRangePretty, formatFYLabel, formatPrettyDate } from "@/lib/useReportRange";
import { exportFinancialPDF, exportFinancialExcel } from "@/lib/exporters";
import YoYSnapshot from "@/components/YoYSnapshot";
import { devError } from "@/lib/logger";

// Stable references — defined at module scope so they don't trigger child re-renders.
const BS_YOY_METRICS = [
  { label: "Total Equity & Liabilities", path: "equity_and_liabilities.total", varKey: "total_equity_liab" },
  { label: "Total Assets", path: "assets.total", varKey: "total_assets" },
  { label: "Owners' Funds", path: "equity_and_liabilities.owners_funds.total", varKey: "owners_funds" },
  { label: "Current Assets", path: "assets.current_assets.total", varKey: "current_assets" },
];

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
const todayStr = () => new Date().toISOString().slice(0, 10);

function fmt(amount) {
  if (amount === 0 || amount == null) return "—";
  return formatINR(amount);
}

function GroupBlock({ romanIndex, heading, subgroups, applied }) {
  return (
    <>
      <tr className="bg-gray-50 border-t-2 border-gray-300">
        <td className="font-mono text-xs text-gray-600 py-2 pl-3" style={{ width: 28 }}>{ROMAN[romanIndex]}</td>
        <td className="font-semibold text-gray-900 py-2" colSpan={2}>{heading}</td>
        <td className="py-2"></td>
      </tr>
      {subgroups.map((sg, i) => (
        <SubGroup key={sg.key} letter={String.fromCharCode(65 + i)} sub={sg} applied={applied} />
      ))}
    </>
  );
}

function SubGroup({ letter, sub, applied }) {
  return (
    <>
      <tr>
        <td></td>
        <td className="pl-5 text-gray-700 text-xs">
          <span className="font-mono text-gray-400 mr-2">({letter})</span>
          <span className="font-medium">{sub.label}</span>
        </td>
        <td className="text-right text-[10px] text-gray-400 font-mono pr-3" data-testid={`note-${sub.key}`}>—</td>
        <td className="tx-num font-mono tabular-nums" data-testid={`amt-${sub.key}`}>{fmt(sub.total)}</td>
      </tr>
      {/* Expand each line item as faint sub-rows (zero rows already filtered) */}
      {sub.rows.map((r, i) => (
        <tr key={`${sub.key}-${i}`} className="text-[11px] text-gray-500">
          <td></td>
          <td className="pl-12 text-gray-500">
            {r.id ? (
              <Link
                to={`/reports/ledger/${r.id}?start=${applied?.start || ""}&end=${applied?.end || ""}`}
                className="text-blue-700 hover:underline"
                data-testid={`link-bs-ledger-${r.label}`}
                title="Open this ledger's statement — edits there will update this Balance Sheet."
              >
                {r.label}
              </Link>
            ) : r.label}
          </td>
          <td></td>
          <td className="text-right pr-3 font-mono tabular-nums">{fmt(r.amount)}</td>
        </tr>
      ))}
    </>
  );
}

function Side({ title, sectionKey, data, applied }) {
  const blocks = sectionKey === "equity_and_liabilities"
    ? [
        { key: "owners_funds", label: "Owners' Funds", subItems: [{ key: "of", label: "Owners' Capital Account & Reserves and Surplus" }] },
        { key: "non_current_liabilities", label: "Non-current Liabilities" },
        { key: "current_liabilities", label: "Current Liabilities" },
      ]
    : [
        { key: "non_current_assets", label: "Non-current Assets" },
        { key: "current_assets", label: "Current Assets" },
      ];

  // We render each block with its own roman numeral. Within a block, each "row" is a sub-group with label + amount.
  return (
    <div className="border border-gray-200 rounded bg-white overflow-hidden" data-testid={`bs-side-${sectionKey}`}>
      <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 text-xs font-semibold text-gray-900 uppercase tracking-wider">{title}</div>
      <table className="w-full">
        <thead>
          <tr className="text-[10px] uppercase text-gray-500 border-b border-gray-200">
            <th className="text-left pl-3 py-1.5 font-medium" style={{ width: 28 }}>#</th>
            <th className="text-left py-1.5 font-medium">Particulars</th>
            <th className="text-right py-1.5 font-medium pr-3 w-12">Note</th>
            <th className="text-right py-1.5 font-medium pr-3 w-40">Amount (₹)</th>
          </tr>
        </thead>
        <tbody>
          {blocks.map((b, i) => (
            <GroupBlock
              key={b.key}
              romanIndex={i}
              heading={b.label}
              applied={applied}
              subgroups={data[b.key].rows.length ? data[b.key].rows.map((r) => ({
                key: `${b.key}-${r.label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`,
                label: r.label,
                total: r.amount,
                rows: (r.ledgers || []).map((l) => ({ label: l.label || l.name, amount: l.amount, id: l.id })),
              })) : [{ key: `${b.key}-empty`, label: "—", total: 0, rows: [] }]}
            />
          ))}
          <tr className="bg-blue-50 border-t-2 border-blue-300 font-semibold">
            <td colSpan={3} className="pl-3 py-2.5 text-gray-900 uppercase tracking-wider text-xs">Total ({title})</td>
            <td className="tx-num text-blue-900 pr-3 font-mono tabular-nums text-base" data-testid={`total-${sectionKey}`}>{fmt(data.total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function Inner() {
  const { activeId, active } = useCompany();
  const { range, setRange, applied, apply, params } = useReportRange();
  const dirty = range.start !== applied.start || range.end !== applied.end;
  const [data, setData] = useState(null);

  const bsYoyParams = useMemo(() => ({ end: applied.end }), [applied.end]);

  useEffect(() => {
    if (!activeId) return;
    let mounted = true;
    api.get(`/companies/${activeId}/reports/balance-sheet`, { params })
      .then((r) => { if (mounted) setData(r.data); })
      .catch((err) => devError("BS load failed", err));
    return () => { mounted = false; };
  }, [activeId, applied.start, applied.end, params]);

  const onExcel = () => {
    if (!data) return;
    const buildSheet = (sec, title) => {
      const blocks = sec === "equity_and_liabilities"
        ? [["I", "Owners' Funds", "owners_funds"], ["II", "Non-current Liabilities", "non_current_liabilities"], ["III", "Current Liabilities", "current_liabilities"]]
        : [["I", "Non-current Assets", "non_current_assets"], ["II", "Current Assets", "current_assets"]];
      const out = [];
      blocks.forEach(([rn, head, key]) => {
        out.push({ cells: [rn, head, "", ""], type: "group" });
        data[sec][key].rows.forEach((r) => {
          out.push({ cells: ["", r.label, "", r.amount], type: "sub" });
          (r.ledgers || []).forEach((l) => out.push({ cells: ["", "    " + l.name, "", l.amount], type: "ledger" }));
        });
        out.push({ cells: ["", `Total — ${head}`, "", data[sec][key].total], type: "total" });
      });
      out.push({ cells: ["", `TOTAL ${title.toUpperCase()}`, "", data[sec].total], type: "grand" });
      return { name: title, title, headers: ["#", "Particulars", "Note", "Amount (₹)"], rows: out };
    };
    exportFinancialExcel(`balance-sheet-${active?.name || "report"}`, [
      buildSheet("equity_and_liabilities", "Equity and Liabilities"),
      buildSheet("assets", "Assets"),
    ], { company: active?.name, subtitle: `Balance Sheet as on ${applied.end || todayStr()} — Schedule III (Non-Corporate)` });
  };

  const onPDF = () => {
    if (!data) return;
    const buildRows = (sec) => {
      const blocks = sec === "equity_and_liabilities"
        ? [["I", "Owners' Funds", "owners_funds"], ["II", "Non-current Liabilities", "non_current_liabilities"], ["III", "Current Liabilities", "current_liabilities"]]
        : [["I", "Non-current Assets", "non_current_assets"], ["II", "Current Assets", "current_assets"]];
      const out = [];
      blocks.forEach(([rn, head, key]) => {
        out.push({ cells: [rn, head, ""], type: "group" });
        data[sec][key].rows.forEach((r) => {
          out.push({ cells: ["", r.label, formatINR(r.amount)], type: "sub" });
          (r.ledgers || []).forEach((l) => out.push({ cells: ["", l.name, formatINR(l.amount)], type: "ledger" }));
        });
        out.push({ cells: ["", `Total — ${head}`, formatINR(data[sec][key].total)], type: "total" });
      });
      out.push({ cells: ["", `TOTAL`, formatINR(data[sec].total)], type: "grand" });
      return out;
    };
    const _pretty = (iso) => formatPrettyDate(iso) || iso || todayStr();
    exportFinancialPDF(`balance-sheet-${active?.name || "report"}`,
      "Balance Sheet",
      [
        { title: "Equity and Liabilities", headers: ["#", "Particulars", "Amount (Rs.)"], rows: buildRows("equity_and_liabilities") },
        { title: "Assets", headers: ["#", "Particulars", "Amount (Rs.)"], rows: buildRows("assets") },
      ],
      {
        company: active?.name,
        address: active?.address,
        phone: active?.phone,
        email: active?.email,
        gstin: active?.gstin,
        pan: active?.pan,
        fy: formatFYLabel(range),
        subtitle: `As at ${_pretty(applied.end || todayStr())}  -  Schedule III (Division I) Non-Corporate Entity`,
        date: applied.end,
        place: active?.state || "",
        showSignatures: true,
      });
  };

  return (
    <>
      <PageHeader
        title="Balance Sheet"
        subtitle={`${active?.name || ""}  •  As at ${formatPrettyDate(applied.end) || applied.end || "—"}  •  Schedule III (Non-Corporate)`}
        actions={<ExportToolbar onExcel={onExcel} onPDF={onPDF} onPrint={() => window.print()} />}
      />
      <div className="p-6 space-y-4" data-testid="balance-sheet-page">
        <DateRangePicker start={range.start} end={range.end} onChange={setRange} onApply={apply} dirty={dirty} />
        {data && (
          <YoYSnapshot
            endpoint={`/companies/${activeId}/reports/balance-sheet`}
            params={bsYoyParams}
            metrics={BS_YOY_METRICS}
            testid="bs-yoy"
          />
        )}
        {!data && <div className="text-sm text-gray-500" data-testid="bs-loading">Loading…</div>}
        {data && (
          <>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <Side title="Equity and Liabilities" sectionKey="equity_and_liabilities" data={data.equity_and_liabilities} applied={applied} />
              <Side title="Assets" sectionKey="assets" data={data.assets} applied={applied} />
            </div>

            {/* Balance check */}
            <div className={`text-xs rounded border p-3 ${
              Math.abs(data.difference) < 0.5
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-amber-200 bg-amber-50 text-amber-800"
            }`} data-testid="bs-balance-check">
              {Math.abs(data.difference) < 0.5
                ? <>Balance check: <span className="font-mono">Equity &amp; Liabilities (₹{formatINR(data.equity_and_liabilities.total)}) = Assets (₹{formatINR(data.assets.total)})</span> ✓</>
                : <>Difference: <span className="font-mono">₹ {formatINR(Math.abs(data.difference))}</span> — usually fixable by booking opening capital or correcting unbalanced opening balances.</>
              }
            </div>

            <div className="text-[10px] text-gray-400 italic">
              The accompanying notes are an integral part of the financial statements. Schedule III (Division I) format for Non-Corporate Entity. Period: {formatRangeLabel(range)}.
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default function BalanceSheet() {
  return (
    <RequireCompany>
      <Inner />
    </RequireCompany>
  );
}
